import React, { useState, useEffect, useContext, useCallback } from "react";
import { getApiUrl } from "../config/api";
import { InternalLayout } from "../Components/InternalLayout";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Chip,
  InputAdornment,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Search as SearchIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
} from "@mui/icons-material";
import { AuthContext } from "../contexts/AuthContext";

function GerenciarMinistros() {
  const { token } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  const [ministros, setMinistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingMinistro, setEditingMinistro] = useState(null);
  const [ministroToDelete, setMinistroToDelete] = useState(null);
  const [search, setSearch] = useState("");
  const [filtroAtivo, setFiltroAtivo] = useState(true);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    type: "info",
  });
  const [formData, setFormData] = useState({
    nome: "",
    telefone: "",
    observacoes: "",
    ativo: true,
  });

  // Função para mostrar alertas
  const showAlert = (message, type = "info") => {
    setAlert({ open: true, message, type });
    setTimeout(
      () => setAlert({ open: false, message: "", type: "info" }),
      5000,
    );
  };

  // Buscar ministros
  const fetchMinistros = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filtroAtivo !== undefined) params.append('ativo', filtroAtivo.toString());

      const response = await fetch(
        getApiUrl(`/api/secretaria/utilidades?action=ministros&${params.toString()}`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setMinistros(data.ministros || []);
      } else {
        const error = await response.json();
        showAlert(error.message || "Erro ao carregar ministros", "error");
      }
    } catch (error) {
      console.error("Erro ao carregar ministros:", error);
      showAlert("Erro ao carregar ministros", "error");
    } finally {
      setLoading(false);
    }
  }, [token, search, filtroAtivo]);

  useEffect(() => {
    fetchMinistros();
  }, [fetchMinistros]);

  // Formatar telefone para exibição
  const formatarTelefone = (telefone) => {
    if (!telefone) return "";
    // 5583987654321 -> +55 (83) 98765-4321
    const clean = telefone.replace(/\D/g, "");
    if (clean.length === 13) {
      return `+${clean.slice(0, 2)} (${clean.slice(2, 4)}) ${clean.slice(4, 9)}-${clean.slice(9)}`;
    }
    return telefone;
  };

  // Abrir dialog
  const handleOpenDialog = (ministro = null) => {
    setEditingMinistro(ministro);
    if (ministro) {
      setFormData({
        nome: ministro.nome,
        telefone: ministro.telefone,
        observacoes: ministro.observacoes || "",
        ativo: ministro.ativo,
      });
    } else {
      setFormData({
        nome: "",
        telefone: "",
        observacoes: "",
        ativo: true,
      });
    }
    setDialogOpen(true);
  };

  // Fechar dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingMinistro(null);
    setFormData({
      nome: "",
      telefone: "",
      observacoes: "",
      ativo: true,
    });
  };

  // Confirmar delete
  const handleDeleteClick = (ministro) => {
    setMinistroToDelete(ministro);
    setDeleteDialogOpen(true);
  };

  // Handle input change
  const handleInputChange = (field) => (event) => {
    if (field === "ativo") {
      setFormData((prev) => ({ ...prev, [field]: event.target.checked }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: event.target.value }));
    }
  };

  // Submeter form
  const handleSubmit = async () => {
    try {
      const method = editingMinistro ? "PUT" : "POST";
      const body = editingMinistro
        ? { ministroId: editingMinistro._id, ...formData }
        : formData;

      const response = await fetch(getApiUrl("/api/secretaria/utilidades?action=ministros"), {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const data = await response.json();
        showAlert(data.message, "success");
        handleCloseDialog();
        fetchMinistros();
      } else {
        const error = await response.json();
        showAlert(error.message || "Erro ao salvar ministro", "error");
      }
    } catch (error) {
      console.error("Erro ao salvar ministro:", error);
      showAlert("Erro ao salvar ministro", "error");
    }
  };

  // Deletar ministro
  const handleDeleteMinistro = async () => {
    try {
      const response = await fetch(getApiUrl("/api/secretaria/utilidades?action=ministros"), {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ministroId: ministroToDelete._id }),
      });

      if (response.ok) {
        const data = await response.json();
        showAlert(data.message, "success");
        setDeleteDialogOpen(false);
        setMinistroToDelete(null);
        fetchMinistros();
      } else {
        const error = await response.json();
        showAlert(error.message || "Erro ao remover ministro", "error");
      }
    } catch (error) {
      console.error("Erro ao remover ministro:", error);
      showAlert("Erro ao remover ministro", "error");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <InternalLayout>
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: "1200px", mx: "auto" }}>
        {/* Alerta */}
        {alert.open && (
          <Alert
            severity={alert.type}
            onClose={() => setAlert({ open: false, message: "", type: "info" })}
            sx={{ mb: 3 }}
          >
            {alert.message}
          </Alert>
        )}

        {/* Cabeçalho */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
            mb: 3,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: { xs: "1.5rem", md: "2.125rem" },
            }}
          >
            <PeopleIcon sx={{ fontSize: { xs: 24, md: 32 } }} />
            Gerenciar Ministros
          </Typography>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              width: { xs: "100%", sm: "auto" },
              py: { xs: 1, md: 1.5 },
            }}
          >
            Novo Ministro
          </Button>
        </Box>

        {/* Filtros */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
              <TextField
                placeholder="Buscar por nome ou telefone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: 1 }}
                size={isMobile ? "small" : "medium"}
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={filtroAtivo}
                    onChange={(e) => setFiltroAtivo(e.target.checked)}
                    color="primary"
                  />
                }
                label="Apenas ativos"
              />
            </Box>
          </CardContent>
        </Card>

        {/* Estatísticas */}
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexDirection: { xs: "column", sm: "row" } }}>
          <Card sx={{ flex: 1 }}>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography color="text.secondary" gutterBottom>
                Total de Ministros
              </Typography>
              <Typography variant="h3" sx={{ fontSize: { xs: "1.75rem", md: "3rem" } }}>
                {ministros.length}
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ flex: 1 }}>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography color="text.secondary" gutterBottom>
                Ativos
              </Typography>
              <Typography variant="h3" sx={{ fontSize: { xs: "1.75rem", md: "3rem" }, color: "success.main" }}>
                {ministros.filter(m => m.ativo).length}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Tabela Desktop */}
        <TableContainer 
          component={Paper} 
          sx={{ 
            display: { xs: "none", md: "block" },
            overflowX: "auto" 
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Nome</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Telefone</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Envios</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ministros.length > 0 ? (
                ministros.map((ministro) => (
                  <TableRow key={ministro._id} hover>
                    <TableCell>{ministro.nome}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <PhoneIcon fontSize="small" color="action" />
                        {formatarTelefone(ministro.telefone)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ministro.ativo ? "Ativo" : "Inativo"}
                        color={ministro.ativo ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{ministro.totalEnvios || 0}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleOpenDialog(ministro)}
                        size="small"
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteClick(ministro)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Nenhum ministro encontrado
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Cards Mobile */}
        <Box sx={{ display: { xs: "block", md: "none" } }}>
          {ministros.length > 0 ? (
            ministros.map((ministro) => (
              <Card key={ministro._id} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold", flex: 1 }}>
                      {ministro.nome}
                    </Typography>
                    <Chip
                      label={ministro.ativo ? "Ativo" : "Inativo"}
                      color={ministro.ativo ? "success" : "default"}
                      size="small"
                    />
                  </Box>
                  
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <WhatsAppIcon fontSize="small" color="success" />
                    <Typography variant="body2">
                      {formatarTelefone(ministro.telefone)}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Envios: {ministro.totalEnvios || 0}
                  </Typography>
                  
                  {ministro.observacoes && (
                    <Typography variant="body2" sx={{ mb: 2, fontStyle: "italic" }}>
                      {ministro.observacoes}
                    </Typography>
                  )}

                  <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                    <Button
                      startIcon={<EditIcon />}
                      onClick={() => handleOpenDialog(ministro)}
                      size="small"
                      variant="outlined"
                    >
                      Editar
                    </Button>
                    <Button
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteClick(ministro)}
                      size="small"
                      variant="outlined"
                      color="error"
                    >
                      Excluir
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent sx={{ textAlign: "center", py: 4 }}>
                <PeopleIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Nenhum ministro encontrado
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>

        {/* Dialog de criar/editar */}
        <Dialog 
          open={dialogOpen} 
          onClose={handleCloseDialog}
          maxWidth="sm" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            {editingMinistro ? "Editar Ministro" : "Novo Ministro"}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Nome completo"
              fullWidth
              variant="outlined"
              value={formData.nome}
              onChange={handleInputChange("nome")}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Telefone"
              fullWidth
              variant="outlined"
              placeholder="5583987654321"
              value={formData.telefone}
              onChange={handleInputChange("telefone")}
              helperText="Formato: 5583987654321 (com código do país e DDD)"
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Observações"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={formData.observacoes}
              onChange={handleInputChange("observacoes")}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.ativo}
                  onChange={handleInputChange("ativo")}
                  color="primary"
                />
              }
              label="Ministro ativo"
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained"
              disabled={!formData.nome || !formData.telefone}
            >
              {editingMinistro ? "Salvar" : "Criar"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de confirmação de delete */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Confirmar exclusão</DialogTitle>
          <DialogContent>
            <Typography>
              Tem certeza que deseja excluir o ministro{" "}
              <strong>{ministroToDelete?.nome}</strong>?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleDeleteMinistro} color="error" variant="contained">
              Excluir
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </InternalLayout>
  );
}

export default GerenciarMinistros;